import { getIdToken } from './authService';

interface Bot {
  botId: string;
  botName?: string;  // Optional for backward compatibility with legacy records
  role: string;
  grantedAt: string;
  grantedBy: string;
}

interface ListBotsResponse {
  bots: Bot[];
  count: number;
}

interface PushTopicRequest {
  botId: string;
  userId?: string;
  topicContent?: string;
  topicId?: string;
  originalImageUrl?: string;
  previewImageUrl?: string;
  pdfFileUrl?: string;
  pdfPreviewUrl?: string;
  pdfFileName?: string;
  pdfFileSize?: number;
  pdfPageCount?: number;
  referenceContext?: string;
  referenceContextExpiresAt?: string;
}

interface PushTopicResponse {
  success?: boolean;
  message: string;
  responseId?: string;
  pushedBy?: string;
  source?: string;
}

async function makeAuthenticatedRequest<T>(
  url: string,
  method: string,
  body: any = null
): Promise<T> {
  const idToken = await getIdToken();

  if (!idToken) {
    throw new Error('Not authenticated');
  }

  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error (${response.status}): ${errorText}`);
  }

  return await response.json();
}

export async function pushTopic(
  botId: string,
  userId: string | undefined,
  topicContent: string | undefined,
  topicId?: string,
  referenceContext?: string,
  referenceContextExpiresAt?: string,
  originalImageUrl?: string,
  previewImageUrl?: string,
  pdfFileUrl?: string,
  pdfPreviewUrl?: string,
  pdfFileName?: string,
  pdfFileSize?: number,
  pdfPageCount?: number
): Promise<PushTopicResponse> {
  const url = `${process.env.REACT_APP_ADMIN_API_URL}/admin/push-topic`;

  const body: PushTopicRequest = {
    botId,
  };

  if (topicContent) {
    body.topicContent = topicContent;
  }

  if (userId) {
    body.userId = userId;
  }

  if (topicId) {
    body.topicId = topicId;
  }

  if (originalImageUrl) {
    body.originalImageUrl = originalImageUrl;
  }

  if (previewImageUrl) {
    body.previewImageUrl = previewImageUrl;
  }

  if (pdfFileUrl) {
    body.pdfFileUrl = pdfFileUrl;
  }

  if (pdfPreviewUrl) {
    body.pdfPreviewUrl = pdfPreviewUrl;
  }

  if (pdfFileName) {
    body.pdfFileName = pdfFileName;
  }

  if (pdfFileSize) {
    body.pdfFileSize = pdfFileSize;
  }

  if (pdfPageCount) {
    body.pdfPageCount = pdfPageCount;
  }

  if (referenceContext) {
    body.referenceContext = referenceContext;
  }

  if (referenceContextExpiresAt) {
    body.referenceContextExpiresAt = referenceContextExpiresAt;
  }

  return await makeAuthenticatedRequest<PushTopicResponse>(url, 'POST', body);
}

export async function listBots(): Promise<ListBotsResponse> {
  const url = `${process.env.REACT_APP_ADMIN_API_URL}/admin/bots`;
  return await makeAuthenticatedRequest<ListBotsResponse>(url, 'GET');
}

interface GenerateImageUploadUrlResponse {
  originalUploadUrl: string;
  previewUploadUrl: string;
  originalImageUrl: string;
  previewImageUrl: string;
  originalKey: string;
  previewKey: string;
  expiresIn: number;
}

/**
 * Generate presigned URLs for uploading original and preview images to S3
 */
export async function generateImageUploadUrl(
  botId: string,
  fileName: string,
  originalFileSize: number,
  previewFileSize: number,
  contentType: string
): Promise<GenerateImageUploadUrlResponse> {
  const url = `${process.env.REACT_APP_ADMIN_API_URL}/admin/generate-image-upload-url`;

  const body = {
    botId,
    fileName,
    originalFileSize,
    previewFileSize,
    contentType,
  };

  return await makeAuthenticatedRequest<GenerateImageUploadUrlResponse>(url, 'POST', body);
}

interface GenerateFileUploadUrlResponse {
  fileUploadUrl: string;
  previewUploadUrl: string;
  fileUrl: string;
  previewUrl: string;
  fileKey: string;
  previewKey: string;
  expiresIn: number;
}

/**
 * Generate presigned URLs for uploading PDF file and preview image to S3
 */
export async function generateFileUploadUrl(
  botId: string,
  fileName: string,
  fileSize: number,
  previewFileSize: number,
  contentType: string
): Promise<GenerateFileUploadUrlResponse> {
  const url = `${process.env.REACT_APP_ADMIN_API_URL}/admin/generate-file-upload-url`;

  const body = {
    botId,
    fileName,
    fileSize,
    previewFileSize,
    contentType,
  };

  return await makeAuthenticatedRequest<GenerateFileUploadUrlResponse>(url, 'POST', body);
}

/**
 * Upload image file to S3 using presigned URL
 */
export async function uploadImageToS3(
  presignedUrl: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const progress = Math.round((e.loaded / e.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('timeout', () => {
      reject(new Error('Upload timeout'));
    });

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.timeout = 60000; // 60 second timeout
    xhr.send(file);
  });
}

// ── Feature flags ──────────────────────────────────────────────────────────

export interface AnalyticsChatFeature {
  enabled: boolean;
  origin?: string;
  integrationId?: string;
}

export interface AdminFeatures {
  analytics: boolean;
  analyticsChat: AnalyticsChatFeature;
}

/**
 * Fetch per-admin feature flags from GET /admin/features.
 * Fails open — returns { analytics: true, analyticsChat: { enabled: false } }
 * on any error so the UI never incorrectly hides a feature that the backend
 * would allow.
 */
export async function getFeatures(): Promise<AdminFeatures> {
  try {
    const url = `${process.env.REACT_APP_ADMIN_API_URL}/admin/features`;
    const result = await makeAuthenticatedRequest<{ features: AdminFeatures }>(url, 'GET');
    const features = result.features ?? { analytics: true, analyticsChat: { enabled: false } };
    // Ensure analyticsChat always has a defined shape even if absent from backend response
    return {
      ...features,
      analyticsChat: features.analyticsChat ?? { enabled: false },
    };
  } catch {
    return { analytics: true, analyticsChat: { enabled: false } };
  }
}

// ── Analytics types ────────────────────────────────────────────────────────

export interface AnalyticsOverview {
  total_messages: number;
  active_users: number;
  user_messages: number;
  assistant_messages: number;
  line_messages: number;
  webchat_messages: number;
  pushed_messages: number;
}

export interface DailyMessage {
  day: string;
  total: number;
  user_messages: number;
  assistant_messages: number;
  active_users: number;
}

export interface TopUser {
  user_id: string;
  total_messages: number;
  user_messages: number;
  first_message_at: string;
  last_message_at: string;
}

export interface BroadcastStat {
  broadcast_id: string;
  sent_at: string;
  bot_id: string;
  topic_id: string;
  topic_preview: string;
  status: string;
  triggered_by: string;
  fanout_user_count: number;
}

export interface AnalyticsFilters {
  botId: string;
  from?: string;
  to?: string;
}

interface AnalyticsResponse<T> {
  action: string;
  filters: AnalyticsFilters;
  data: T;
}

/**
 * Build analytics URL with query params.
 * botId is required; from/to are optional ISO date strings.
 */
function analyticsUrl(action: string, filters: AnalyticsFilters): string {
  const base = `${process.env.REACT_APP_ADMIN_API_URL}/admin/analytics/${action}`;
  const params = new URLSearchParams();
  params.set('botId', filters.botId);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  return `${base}?${params.toString()}`;
}

/**
 * Surfaces 503 as AnalyticsUnavailableError so the UI can show a friendly
 * "not configured" message rather than a generic error.
 */
export class AnalyticsUnavailableError extends Error {
  constructor() {
    super('Analytics not available');
    this.name = 'AnalyticsUnavailableError';
  }
}

/**
 * Surfaces a per-bot 403 as AnalyticsForbiddenError so the UI can show an
 * access-denied message for the selected bot.
 */
export class AnalyticsForbiddenError extends Error {
  constructor() {
    super('Access denied for this bot');
    this.name = 'AnalyticsForbiddenError';
  }
}

/**
 * Surfaces a feature-flag 403 (code === 'analytics_disabled') as
 * AnalyticsDisabledError so the UI can show a dedicated "feature not enabled"
 * message distinct from the per-bot access-denied message.
 */
export class AnalyticsDisabledError extends Error {
  constructor() {
    super('Analytics is not enabled for your account');
    this.name = 'AnalyticsDisabledError';
  }
}

async function makeAnalyticsRequest<T>(
  action: string,
  filters: AnalyticsFilters
): Promise<T> {
  const idToken = await getIdToken();
  if (!idToken) throw new Error('Not authenticated');

  const url = analyticsUrl(action, filters);
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (response.status === 503) {
    throw new AnalyticsUnavailableError();
  }

  if (response.status === 403) {
    // Try to parse the body to distinguish analytics_disabled from bot-level denial
    try {
      const body = await response.json();
      if (body?.code === 'analytics_disabled') {
        throw new AnalyticsDisabledError();
      }
    } catch (e) {
      if (e instanceof AnalyticsDisabledError) throw e;
      // JSON parse failed — fall through to generic forbidden
    }
    throw new AnalyticsForbiddenError();
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error (${response.status}): ${errorText}`);
  }

  const body: AnalyticsResponse<T> = await response.json();
  return body.data;
}

export async function getAnalyticsOverview(
  filters: AnalyticsFilters
): Promise<AnalyticsOverview> {
  return makeAnalyticsRequest<AnalyticsOverview>('overview', filters);
}

export async function getAnalyticsDailyMessages(
  filters: AnalyticsFilters
): Promise<DailyMessage[]> {
  return makeAnalyticsRequest<DailyMessage[]>('messages-daily', filters);
}

export async function getAnalyticsTopUsers(
  filters: AnalyticsFilters
): Promise<TopUser[]> {
  return makeAnalyticsRequest<TopUser[]>('top-users', filters);
}

export async function getAnalyticsBroadcasts(
  filters: AnalyticsFilters
): Promise<BroadcastStat[]> {
  return makeAnalyticsRequest<BroadcastStat[]>('broadcasts', filters);
}

export interface UserMessage {
  message_timestamp: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  source: 'line' | 'webchat';
  message_source: string;
  session_id: string;
  topic_id: string;
}

export async function getAnalyticsUserMessages(
  filters: AnalyticsFilters & { userId: string }
): Promise<UserMessage[]> {
  const idToken = await getIdToken();
  if (!idToken) throw new Error('Not authenticated');

  const base = `${process.env.REACT_APP_ADMIN_API_URL}/admin/analytics/user-messages`;
  const params = new URLSearchParams();
  params.set('botId', filters.botId);
  params.set('userId', filters.userId);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  const url = `${base}?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (response.status === 503) {
    throw new AnalyticsUnavailableError();
  }

  if (response.status === 403) {
    try {
      const errBody = await response.json();
      if (errBody?.code === 'analytics_disabled') {
        throw new AnalyticsDisabledError();
      }
    } catch (e) {
      if (e instanceof AnalyticsDisabledError) throw e;
    }
    throw new AnalyticsForbiddenError();
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error (${response.status}): ${errorText}`);
  }

  const body: AnalyticsResponse<UserMessage[]> = await response.json();
  return body.data;
}

// ── Singleton export ────────────────────────────────────────────────────────

// Create a singleton instance for exports
export const adminApiService = {
  generateImageUploadUrl,
  generateFileUploadUrl,
  uploadImageToS3,
  pushTopic,
  listBots,
  getFeatures,
  getAnalyticsOverview,
  getAnalyticsDailyMessages,
  getAnalyticsTopUsers,
  getAnalyticsBroadcasts,
  getAnalyticsUserMessages,
};

export type {
  Bot,
  ListBotsResponse,
  PushTopicRequest,
  PushTopicResponse,
  GenerateImageUploadUrlResponse,
  GenerateFileUploadUrlResponse
};
